'use client'

import * as React from 'react'
import { GridIcon } from '@/src/icons'
import { cn } from '@/registry/reapit/lib/utils'
import { Button } from '@/registry/reapit/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/registry/reapit/ui/dropdown-menu'

type AppItem = {
  id: string
  name: string
  /** Optional secondary description (e.g. "Property management") */
  description?: string
  /** App icon — DS v5 renders this at 40px (size-10) in a coloured avatar */
  icon?: React.ReactNode
  href?: string
  onClick?: () => void
}

type AppSection = {
  label: string
  items: AppItem[]
}

type AppSwitcherMenuProps = {
  sections: AppSection[]
  trigger?: React.ReactNode
  align?: 'start' | 'center' | 'end'
}

// DS v5 AppSwitcher: trigger is a nav-icon-button with AppSwitcherIcon (24px),
// padding 8px, border-radius 4px, icon colour #9faebc, hover bg #f2f4f6.
// Menu: placement bottom-start, items use grid layout with app-avatar (40px) + name + supplementary info.
function AppSwitcherMenu({ sections, trigger, align = 'start' }: AppSwitcherMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button
            variant="tertiary"
            size="icon"
            aria-label="App Switcher"
            className="rounded-sm [&_svg:not([class*='size-'])]:size-6"
          >
            <GridIcon />
          </Button>
        )}
      </DropdownMenuTrigger>
      {/* DS v5: menu width accommodates app avatars + descriptions */}
      <DropdownMenuContent align={align} className="w-72">
        {sections.map((section, i) => (
          <React.Fragment key={section.label}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
              {section.label}
            </DropdownMenuLabel>
            {section.items.map((item) => {
              const content = (
                <>
                  {/* DS v5: AppAvatar is size-10 (40px) */}
                  {item.icon && (
                    <span className="flex size-10 shrink-0 items-center justify-center [&>svg]:size-full">
                      {item.icon}
                    </span>
                  )}
                  <span className="flex min-w-0 flex-col gap-0.5">
                    {/* DS v5: app name font('sm', 'medium') = 14px/500, colour #222b33 */}
                    <span className="truncate text-sm font-medium">{item.name}</span>
                    {/* DS v5: supplementary info font('xs', 'regular') = 12px/400, colour #607890 */}
                    {item.description && (
                      <span className="truncate text-xs text-muted-foreground">{item.description}</span>
                    )}
                  </span>
                </>
              )
              return (
                <DropdownMenuItem
                  key={item.id}
                  onClick={item.onClick}
                  // DS v5: gap --spacing-3 (12px), padding 12px block / 12px-16px inline
                  className="gap-3 py-3 px-3"
                  asChild={!!item.href}
                >
                  {item.href ? <a href={item.href}>{content}</a> : content}
                </DropdownMenuItem>
              )
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { AppSwitcherMenu }
export type { AppSwitcherMenuProps, AppSection, AppItem }
